// Dataset access. Two sources behind one function:
//   api     -> GET /api/hd/<dataset>?period=&channels=  (backend, slice A/B)
//   fixture -> fixtures/hd.json (mockup data.js extract), loaded once and sliced by name
import { useQueries, useQuery } from '@tanstack/react-query';
import { api, ApiError } from './client';
import type { DatasetName, HdDatasets } from './types/hd';

export type DataMode = 'api' | 'fixture';
export const dataMode: DataMode = import.meta.env.VITE_DATA_MODE === 'fixture' ? 'fixture' : 'api';

let fixturePromise: Promise<Partial<HdDatasets>> | null = null;
function loadFixture(): Promise<Partial<HdDatasets>> {
  fixturePromise ??= fetch(`${import.meta.env.BASE_URL}fixtures/hd.json`)
    .then(async (res) => {
      if (!res.ok) throw new ApiError(res.status, 'fixture_missing', 'Fixture data not found. Run `npm run fixture`.');
      return (await res.json()) as Partial<HdDatasets>; // typed boundary: the fixture is HD as extracted
    })
    .catch((err: unknown) => {
      fixturePromise = null;
      throw err;
    });
  return fixturePromise;
}

export async function fetchDataset<K extends DatasetName>(name: K, signal?: AbortSignal): Promise<HdDatasets[K]> {
  if (dataMode === 'fixture') {
    const hd = await loadFixture();
    const value = hd[name];
    if (value === undefined) throw new ApiError(404, 'unknown_dataset', `Dataset "${name}" is not in the fixture.`);
    return value as HdDatasets[K];
  }
  // period/channels are part of the contract but advisory in v1: the server returns the full
  // dataset shape and screens filter client-side (see docs/API-CONTRACT.md).
  return api.get<HdDatasets[K]>(`/api/hd/${name}`, undefined, signal);
}

const STALE = 5 * 60 * 1000;
export const datasetKey = (name: DatasetName) => ['hd', dataMode, name] as const;

export function useDataset<K extends DatasetName>(name: K) {
  return useQuery({
    queryKey: datasetKey(name),
    queryFn: ({ signal }) => fetchDataset(name, signal),
    staleTime: STALE,
  });
}

export type Picked<K extends DatasetName> = { [P in K]: HdDatasets[P] };
export interface DatasetsResult<K extends DatasetName> {
  data: Picked<K> | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Load several datasets at once; `data` is defined only when every one has arrived. */
export function useDatasets<K extends DatasetName>(names: readonly K[]): DatasetsResult<K> {
  return useQueries({
    queries: names.map((name) => ({
      queryKey: datasetKey(name),
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchDataset(name, signal),
      staleTime: STALE,
    })),
    combine: (results) => {
      const error = (results.find((r) => r.error)?.error as Error | undefined) ?? null;
      const ready = results.every((r) => r.data !== undefined);
      let data: Picked<K> | undefined;
      if (ready) {
        const out: Partial<Picked<K>> = {};
        names.forEach((n, i) => {
          (out as Record<string, unknown>)[n] = results[i]?.data;
        });
        data = out as Picked<K>;
      }
      return {
        data,
        isLoading: !ready && !error,
        error,
        refetch: () => results.forEach((r) => void r.refetch()),
      };
    },
  });
}
